<template>
  <div>
    <slot name="filter"></slot>

    <vs-table
      :sst="true"
      :current-page="model.current_page"
      @change-page="handleChangePage"
      @sort="handleSort"
      @selected="$emit('selected', $event)"
      v-model="itemSelected"
      pagination
      :max-items="maxItems"
      :data="model.data"
      :total="totalItems"
      :no-data-text="noDataText"
      current-sort-type="asc"
      :class="tableStyle"
      :max="paginationMaxItems"
    >
      <template slot="header">
        <div class="flex flex-wrap w-full">
          <div class="vs-row w-full pb-2 gap-2 flex items-end">
            <div class="flex-grow">
              <slot name="filterZone"> </slot>
            </div>

            <div
              v-if="!hide_searchbar"
              class="flex w-full sm:w-auto md:w-1/2 lg:w-3/4 xl:w-3/5 gap-1"
            >
              <b-input-group class="searchbar">
                <template #prepend>
                  <b-input-group-text>
                    <b-icon-search></b-icon-search>
                  </b-input-group-text>
                </template>
                <b-form-input
                  v-model="searchInput"
                  @input="handleSearch(searchInput)"
                  placeholder="Pesquisar"
                >
                </b-form-input>
                <b-input-group-append v-if="searchInput">
                  <b-input-group-text
                    class="cursor-pointer"
                    @click="clearSearch"
                  >
                    <b-icon-x></b-icon-x>
                  </b-input-group-text>
                </b-input-group-append>
              </b-input-group>
              <button-alternates
                v-if="showExport"
                label="Exportar"
                buttonClass="px-4"
                :alternatives="[
                  {
                    icon: 'picture_as_pdf',
                    label: 'PDF',
                    color: 'red',
                    click: () => {
                      exportGrid('pdf')
                    }
                  },
                  {
                    icon: 'border_all',
                    label: 'xls',
                    color: 'success',
                    click: () => {
                      exportGrid('xls')
                    }
                  }
                ]"
                @click-main="exportGrid('pdf')"
              />
              <div class="w-full mt-2 text-right" v-if="showTotalText">
                <b>{{ showTotalText }}:</b> {{ totalItems }}
              </div>
            </div>
          </div>
          <div class="w-full">
            <slot name="gridSubHeader"> </slot>
          </div>
        </div>
      </template>

      <template slot="thead" v-if="theadShow">
        <slot
          name="gridThead"
          :data="columns"
          :methods="{ getColumnName, getColumnSortKey }"
        >
          <vs-th class="w-10" :sort-key="null" v-if="showMultipleSelect">
            <b-form-checkbox v-model="headerSelected" />
          </vs-th>
          <vs-th
            v-for="(column, key) in columns"
            :key="key"
            :sort-key="getColumnSortKey(key)"
            sort-status="asc"
            >{{ $t(getColumnName(column)) }}</vs-th
          >
        </slot>
        <slot name="gridActions">
          <vs-th v-if="!hide_actions" class="no-sort">{{
            $t('actions')
          }}</vs-th>
        </slot>
      </template>

      <template slot-scope="{ data }">
        <slot
          name="gridData"
          v-bind:data="data"
          v-bind:destroy="destroy"
          v-bind:edit="edit"
          v-bind:fetchGridData="fetchGridData"
          v-bind:columns="columns"
          v-bind:methods="{ getColumnCss, getColumnData }"
        >
          <template v-for="(tr, indextr) in data">
            <vs-tr :data="tr" :key="indextr">
              <vs-td v-if="showMultipleSelect">
                <b-form-checkbox
                  :value="tr"
                  v-model="selectedItems"
                  v-if="selectObjectFull"
                />
                <b-form-checkbox
                  :value="tr.id"
                  v-model="selectedItems"
                  v-else
                />
              </vs-td>
              <vs-td
                :data="data[indextr].id"
                v-for="(column, key) in columns"
                :key="key"
              >
                <slot
                  :name="`${key}-column`"
                  :row="data[indextr]"
                  :value="getColumnData(data[indextr], key)"
                >
                  <div :class="getColumnCss(data[indextr], key)">
                    <div
                      v-html-safe="
                        `<div>${getColumnData(data[indextr], key)}</div>`
                      "
                    />
                  </div>
                </slot>
              </vs-td>
              <vs-td v-if="!hide_actions">
                <slot
                  name="actions"
                  :row="tr"
                  :data="data"
                  :indextr="indextr"
                  :methods="{ edit, destroy }"
                >
                  <feather-icon
                    v-permission="`${route_name}.edit`"
                    icon="EditIcon"
                    class="cursor-pointer h-5 w-5 mb-1 mr-1 stroke-current text-warning"
                    v-if="!hide_edit"
                    @click="edit(data[indextr].id)"
                  />
                  <feather-icon
                    icon="EyeIcon"
                    class="cursor-pointer h-5 w-5 mb-1 mr-1 stroke-current text-success"
                    v-if="!hide_show"
                  />
                  <feather-icon
                    v-permission="`${route_name}.delete`"
                    icon="Trash2Icon"
                    class="cursor-pointer h-5 w-5 mb-1 mr-1 stroke-current text-danger"
                    v-if="!hide_delete"
                    @click="destroy(data[indextr])"
                  />
                </slot>
              </vs-td>
            </vs-tr>
            <slot :name="`after-row-${indextr}`"> </slot>
          </template>
        </slot>
      </template>
    </vs-table>
  </div>
</template>

<script>
import ButtonAlternates from './ButtonAlternates.vue'
import FileDownload from 'js-file-download'
import { mimeType } from '@/util/Util'

import {
  BFormInput,
  BFormCheckbox,
  BIconSearch,
  BIconX,
  BInputGroup,
  BInputGroupText,
  BInputGroupAppend
} from 'bootstrap-vue'

export default {
  components: {
    BFormInput,
    BFormCheckbox,
    BIconSearch,
    BIconX,
    BInputGroup,
    BInputGroupText,
    BInputGroupAppend,
    ButtonAlternates
  },
  props: {
    expandable: {
      type: Boolean,
      default: false
    },
    hide_searchbar: {
      type: Boolean,
      default: false
    },
    hide_actions: {
      type: Boolean,
      default: false
    },
    hide_edit: {
      type: Boolean,
      default: false
    },
    hide_show: {
      type: Boolean,
      default: true
    },
    hide_delete: {
      type: Boolean,
      default: false
    },
    route_name: {
      type: String,
      default: null
    },
    service: {
      type: Object,
      default: null
    },
    route_grid_path: {
      type: String,
      default: ''
    },
    route_delete_path: {
      type: String,
      default: ''
    },
    order_column: {
      type: String,
      default: 'id'
    },
    direction: {
      type: String,
      default: 'asc'
    },
    delegate: {
      type: Object,
      default: null
    },
    theadShow: {
      type: Boolean,
      default: true
    },
    showLoading: {
      type: Boolean,
      default: true
    },
    hidePagination: {
      type: Boolean,
      default: false
    },
    noDataText: {
      type: String,
      default: 'Nenhum registro encontrado.'
    },
    paginationMaxItems: {
      type: Number,
      default: 9
    },
    perPage: {
      type: Number,
      default: 10
    },
    column_formats: {
      type: Object,
      default: () => ({})
    },
    sortKeys: {
      type: Object,
      default: () => ({})
    },
    css_formats: {
      type: Object,
      default: () => ({})
    },
    showTotalText: {
      type: String,
      default: null
    },
    totalFormat: {
      type: Function,
      default: total => `${total}`
    },
    fetchOnMounted: {
      type: Boolean,
      default: true
    },
    showExport: {
      type: Boolean,
      default: false
    },
    exportFilename: {
      type: String,
      default: 'export'
    },
    multipleSelect: {
      type: Boolean,
      default: false
    },
    selectObjectFull: {
      type: Boolean,
      default: false
    },
    selectHook: {
      type: Function,
      default: (ids, vm) => ids
    },
    defaultOrderColumn: {
      type: String,
      default: 'id'
    },
    defaultOrderDirection: {
      type: String,
      default: 'asc'
    },
    extraColumns: {
      type: Array,
      default: () => []
    },
    forceFilters: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      model: {
        current_page: 1,
        data: [],
        last_page: 0,
        next_page_url: '',
        prev_page_url: '',
        to: 10,
        total: 0
      },
      columns_: {},
      filterMode: false,
      filter: {
        items: ['']
      },
      custom_filters: [],
      searchInput: '',
      dataToDestroy: null,
      itemSelected: null,
      exportService: null,
      headerSelected: false,
      selectedItems: []
    }
  },
  computed: {
    columns() {
      const columns = { ...this.columns_ }
      Object.assign(columns, ...this.extraColumns)

      return columns
    },
    showMultipleSelect() {
      return this.multipleSelect
    },
    totalItems() {
      return this.model.total
    },
    maxItems() {
      return this.perPage
    },
    hasFilterZone() {
      return !!this.$slots['filterZone'] || !!this.$scopedSlots['filterZone']
    },
    searchbarDivWidth() {
      return this.hasFilterZone ? 'w-full md:w-1/2' : 'w-full'
    },
    hasFilters() {
      const hasAnyFilter =
        this.filter.items.length !== 0 || this.custom_filters.length !== 0

      return hasAnyFilter
    },
    tableStyle() {
      return this.hidePagination ? 'hidePagination' : ''
    }
  },
  watch: {
    itemSelected(val) {
      if (val) {
        this.handleSelectedItem(val)
      }
    },
    headerSelected(val) {
      if (val === true) {
        if (this._.isArray(this._.get(this.model, 'data'))) {
          this.selectedItems = this.model.data.map(model => {
            return this.selectObjectFull ? model : model.id
          })
        }
      } else {
        this.selectedItems = []
      }
    },
    selectedItems(items) {
      let select = this.selectHook(items, this)
      this.$emit('changedSelection', select)
    }
  },
  methods: {
    handleSelectedItem(item) {
      const delegate = this._.get(this.delegate, 'itemSelected')
      if (delegate && this._.isFunction(delegate)) {
        delegate()(item)
      }
    },
    getParams() {
      return {
        page: this.model.current_page,
        order_column: this.order_column,
        direction: this.direction,
        per_page: this.perPage,
        filters: this.filter,
        custom_filters: { ...this.custom_filters, ...(this.forceFilters || {}) }
      }
    },
    fetchGridData(reset_page = false, params = {}) {
      return new Promise((resolve, reject) => {
        if (this.service) {
          if (this.showLoading) {
            this.$vs.loading()
          }

          if (this.filter && !this.isEmpty(this.searchInput)) {
            this.filter.items[0] = this.searchInput
          } else {
            this.filter.items[0] = ''
          }

          this.service.grid(this.route_grid_path, this.getParams()).then(
            response => {
              this.$vs.loading.close()
              this.$set(this.$data, 'model', response.model)
              this.$set(this.$data, 'columns_', response.columns)
              this.$emit('grid-UPDATED', response)
              const lastPage = this._.get(response, 'model.last_page')

              if (lastPage) {
                this.model.current_page = this._.min([
                  lastPage,
                  this.model.current_page
                ])
              }

              resolve()
            },
            error => {
              this.$vs.loading.close()
              reject()
            }
          )
        } else {
          reject()
        }
      })
    },
    exportGrid(type) {
      if (this.exportService) {
        this.$vs.loading()
        this.exportService
          .exportGrid(this.route_grid_path, type, this.getParams())
          .then(data => {
            this.$vs.loading.close()
            FileDownload(data, `${this.exportFilename}.${type}`, mimeType(type))
          })
      }
    },
    getColumnSortKey(key) {
      if (key in this.sortKeys) {
        return this.sortKeys[key]
      } else {
        return key
      }
    },
    getColumnCss(rowData, column) {
      const columnData = this._.get(rowData, column)
      if (
        column in this.css_formats &&
        this.css_formats[column] instanceof Function
      ) {
        return this.css_formats[column](columnData, rowData)
      }

      return ''
    },
    getColumnData(rowData, column) {
      const columnData = this._.get(rowData, column)
      if (
        column in this.column_formats &&
        this.column_formats[column] instanceof Function
      ) {
        return this.column_formats[column](columnData, rowData)
      }

      if (this._.isNumber(columnData)) return columnData
      else return columnData || ''
    },
    edit(id) {
      this.$router.push(`${this.route_name}/${id}/edit`)
    },
    destroy(data) {
      this.dataToDestroy = data
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: 'Confirmação',
        acceptText: 'Sim',
        cancelText: 'Não',
        text: 'Tem certeza que deseja excluir este registro?',
        accept: this.acceptDestroy
      })
    },
    acceptDestroy() {
      if (this.delegate && this.delegate.destroy) {
        const destroyFunction = this.delegate.destroy()
        const promise = destroyFunction(this.dataToDestroy)
        promise.then(value => {
          if ('reload' in value) {
            this.fetchGridData()
          }
        })
      } else {
        this.$vs.loading()
        this.service.destroy(this.dataToDestroy.id).then(
          response => {
            this.$vs.loading.close()
            this.$vs.notify({
              title: 'Successo!',
              text: 'Operação realizada com sucesso.',
              color: 'success',
              iconPack: 'feather',
              position: 'top-center',
              icon: 'icon-check-circle'
            })

            this.fetchGridData(this.current_page)
          },
          error => {
            this.$vs.loading.close()
          }
        )
      }
    },
    handleSearch(searching) {
      this.searchInput = searching
      if (searching === '') {
        this.filter.items = []
      }
      // Debounce fetch
      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.fetchGridData()
      }, 1000)
    },
    handleFilter(value, filter) {
      this.timeout = setTimeout(() => {
        this.custom_filters[filter] = value
        this.fetchGridData()
      }, 1000)
    },
    handleChangePage(page) {
      this.model.current_page = page
      return this.fetchGridData(page)
    },
    handleSort(key, direction) {
      this.order_column = !this.isEmpty(key)
        ? key.toLowerCase()
        : this.defaultOrderColumn
      if (direction !== null) {
        this.direction = direction
      } else {
        this.direction = this.defaultOrderDirection
      }

      this.$emit('update:direction', this.direction)

      if (direction === null) {
        this.order_column = this.defaultOrderColumn
      }

      this.$emit('update:order_column', this.order_column)

      if (key === null) return
      else this.fetchGridData(true)
    },
    // Updates custom filters from param.
    fillCustomFilters(customFilters) {
      customFilters.forEach(filter => {
        this.custom_filters[filter.key] = filter.value
      })
    },
    // Replaces custom filters and items with params and fetch data.
    flashFilters(items, custom) {
      this.dlog('flashing filters, items> ', items, ' custom> ', custom)
      this.custom_filters = custom
      this.filter.items = items
      return this.handleChangePage(1)
    },
    resetFilters() {
      this.filter.items = []
      this.custom_filters = []
    },
    applySimpleContent(content) {
      this.model.data = content
    },
    getColumnName(column) {
      if (typeof column === 'object') {
        return column.name
      }
      return column
    },
    dlog(...things) {
      this.devLog(`grid:${this.route_name || this.route_grid_path}`, ...things)
    },
    fixPaginationButtons() {
      const left =
        '.vs-pagination--buttons.btn-prev-pagination.vs-pagination--button-prev > i'
      const right =
        '.vs-pagination--buttons.btn-next-pagination.vs-pagination--button-next > i'
      this.$utils.DOM.patchElement(left, 'chevron_left')
      this.$utils.DOM.patchElement(right, 'chevron_right')
    },
    clearSelection() {
      this.selectedItems = []
    },
    clearSearch() {
      this.searchInput = ''
      this.filter.items = []
      this.fetchGridData()
    }
  },
  beforeMount() {
    if (!this.order_column) {
      this.order_column = this.defaultOrderColumn
    }
    if (!this.direction) {
      this.direction = this.defaultOrderDirection
    }
  },
  mounted() {
    if (this.fetchOnMounted) {
      this.fetchGridData()
    }
    if (this.showExport) {
      this.exportService = ReportService.build(this.$vs)
    }
    this.$emit('ready', this)
    this.fixPaginationButtons()
  }
}
</script>

<style>
.searchbar {
  width: 100% !important;
}

.searchbar input {
  height: 38px;
}

.searchbar .cursor-pointer:hover {
  background-color: #f8f9fa;
  color: #dc3545;
}

.hidePagination div.con-vs-pagination {
  display: none;
}
</style>
